'use client';

import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
// Form types available for future use
import { DeliveryFormData } from './order.types';
import { DeliveryData } from '@/types/manage.types';
import { DateField } from './fragments/DateField';
import { useOrder } from './OrderContext';
import { FormField } from '@/components/forms/FormField';
import PhoneNumberInput from '@/components/ui/custom/PhoneNumberInput';
import config from '@/lib/config';
import { TimeField } from './fragments/TimeField';

const DeliveryInformation = () => {
  const {
    state: { deliveryData },
    setDeliveryData,
  } = useOrder();

  const {
    control,
    reset,
    getValues,
    formState: { errors: errors },
  } = useFormContext();

  // Has loaded data from context, on mount (to avoid infinite loop)
  const [hasLoadedData, setHasLoadedData] = useState<boolean>(false);

  // * On mount: restore form values from context state
  useEffect(() => {
    if (!hasLoadedData && deliveryData) {
      // Convert deliveryData to match form schema
      const formData: DeliveryFormData = {
        ...deliveryData,
        contactMethod:
          deliveryData.contactMethod === 'email' || deliveryData.contactMethod === 'phone'
            ? deliveryData.contactMethod
            : 'email', // fallback to email if invalid
        // Ensure deliveryDate is a number
        // deliveryDate:
        //   typeof deliveryData.deliveryDate === 'string'
        //     ? new Date(deliveryData.deliveryDate).getTime()
        //     : deliveryData.deliveryDate,
      };
      console.log('Loading form data from context:', formData);
      reset(formData);
      setHasLoadedData(true);
    }
  }, [reset, deliveryData, hasLoadedData]);

  // * Save form content when the component unmounts
  useEffect(() => {
    return () => {
      const currentData = getValues();
      console.log('Current form data before processing:', currentData);

      const processedData: DeliveryData = {
        // Required fields with defaults if not present
        fullName: currentData.fullName || '',
        phoneNumber: currentData.phoneNumber || '',
        email: currentData.email || '',
        contactMethod:
          currentData.contactMethod === 'email' || currentData.contactMethod === 'phone'
            ? currentData.contactMethod
            : 'email',
        streetAddress: currentData.streetAddress || '',
        city: currentData.city || '',
        state: currentData.state || '',
        zipCode: currentData.zipCode || '',
        // Handle deliveryDate
        deliveryDate: currentData.deliveryDate,
        deliveryTime: currentData.deliveryTime || '',
      };

      console.log('Processed data to save:', processedData);
      setDeliveryData(processedData);
    };
  }, [getValues, setDeliveryData]);

  const stateOptions = useMemo(
    () =>
      config.allowedStates.map((state) => (
        <SelectItem key={state.ab} value={state.ab}>
          {state.name}
        </SelectItem>
      )),
    [],
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact & Delivery Details</h2>
      <p className="text-gray-600 mb-6">
        Please provide your contact information and preferred delivery details below.
      </p>

      <div className="space-y-8">
        {/* Contact Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* {JSON.stringify(errors) !== '{}' && (
              <>
                <div className="md:col-span-2 p-4 border border-red-300 bg-red-50 text-red-700 rounded">
                  Please correct the errors below to proceed.
                </div>
                <div>{JSON.stringify(errors)}</div>
              </>
            )} */}
            <FormField name="fullName" control={control} error={errors.fullName} required>
              <FormField.Label>Full Name</FormField.Label>
              <FormField.Input required placeholder="First name and last name" />
              <FormField.Error />
            </FormField>
            <FormField name="phoneNumber" control={control} error={errors.phoneNumber} required>
              <FormField.Label>Phone Number</FormField.Label>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <PhoneNumberInput
                    id="phoneNumber"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="(555) 123-4567"
                    error={!!errors.phoneNumber}
                  />
                )}
              />
              <FormField.Error />
            </FormField>
            <FormField name="email" control={control} error={errors.email} required>
              <FormField.Label>Email</FormField.Label>
              <FormField.Input type="email" placeholder="Enter your email address" />
              <FormField.Error />
            </FormField>
            <FormField name="contactMethod" control={control} error={errors.contactMethod} required>
              <FormField.Label>Preferred Contact Method</FormField.Label>
              <Controller
                name="contactMethod"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className={errors.contactMethod ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FormField.Error />
            </FormField>
          </div>
        </div>

        {/* Delivery Location Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Delivery Location</h3>
          <p className="text-sm text-gray-600 mb-4">Let us know where to deliver your order.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField name="streetAddress" control={control} error={errors.streetAddress} required>
              <FormField.Label>Street Address</FormField.Label>
              <FormField.Input type="text" placeholder="123 Main St" />
              <FormField.Error />
            </FormField>
            <FormField name="zipCode" control={control} error={errors.zipCode} required>
              <FormField.Label>ZIP Code</FormField.Label>
              <FormField.Input type="text" placeholder="Ex: 10001" maxLength={5} />
              <FormField.Error />
            </FormField>
            <FormField name="city" control={control} error={errors.city} required>
              <FormField.Label>City</FormField.Label>
              <FormField.Input type="text" placeholder="Ex: New York" />
              <FormField.Error />
            </FormField>
            <FormField name="state" control={control} error={errors.state} required>
              <FormField.Label>State</FormField.Label>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className={`w-full ${errors.state ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select a state" />
                    </SelectTrigger>
                    <SelectContent>{stateOptions}</SelectContent>
                  </Select>
                )}
              />
              <FormField.Error />
            </FormField>
          </div>
        </div>

        {/* Delivery Timing Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Delivery Timing</h3>
          <p className="text-sm text-gray-600 mb-4">When would you like your order delivered?</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block text-sm font-medium text-gray-700">
                Preferred Delivery Date{' '}
                <span className="text-red-600/60 ml-1 " aria-label="required">
                  *
                </span>
              </Label>

              <DateField
                name="deliveryDate"
                rules={{
                  required: 'Delivery date is required',
                  validate: (value: number) => {
                    if (!value) return 'Please select a delivery date';

                    const selected = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    // Reset time part for comparison
                    selected.setHours(0, 0, 0, 0);

                    if (selected < today) {
                      return 'Delivery date cannot be in the past';
                    }

                    return true;
                  },
                }}
              />
            </div>
            <div>
              <Label className="mb-2 block text-sm font-medium text-gray-700">
                Preferred Delivery Time{' '}
                <span className="text-red-600/60 ml-1" aria-label="required">
                  *
                </span>
              </Label>
              <TimeField name="deliveryTime" />
              {/* <FormField name="deliveryTime" control={control} error={errors.deliveryTime} required>
              <FormField.Label>Preferred Delivery Time</FormField.Label>
              <FormField.Input type="time" />
              <FormField.Error />
            </FormField> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryInformation;
